<template>
  <div
    :class="[
      $style.layout,
      {
        [$style['layout--wide']]: wide,
        [$style['layout--fullBleed']]: fullBleed,
        [$style['layout--footerSmall']]: footerSize === 'small',
      },
    ]"
  >
    <AppHeader :class="$style.layout__header" :result="result ?? null" />
    <AlertPortal enableAutoHide :class="$style.layout__alert" />
    <ModalManager :viewer="viewer" :contract="contract" />
    <main :class="$style.layout__content">
      <slot v-if="tabActivated" />
    </main>
    <AppFooter
      :fragment="result"
      :size="footerSize"
      :class="$style.layout__footer"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import AppHeader from '@/components/organisms/AppHeader/AppHeader.vue'
import AppFooter from '@/components/organisms/AppFooter/AppFooter.vue'
import DolphinAlert from '@/components/organisms/DolphinAlert/DolphinAlert.vue'
import ModalManager from './ModalManager.vue'

import { alertPortalize } from '@/libs/alertPortal'
import { trackPlanner } from '@/newrelic'

import { useFetchDefaultLayoutQuery } from './DefaultLayout.generated'
import { useOnTabActivated } from '@/hooks/useOnTabActivated'

defineProps<{
  fullBleed?: boolean
  footerSize?: 'small' | 'large'
  wide?: boolean
}>()

const { result } = useFetchDefaultLayoutQuery()
const viewer = computed(() => result.value?.viewer ?? null)
const contract = computed(() => result.value?.contract ?? null)
const AlertPortal = alertPortalize(DolphinAlert)

// NewRelic用のユーザー追跡設定
// ページリロードなどを考慮して、DefaultLayoutでユーザーの状態を監視
watch(
  viewer,
  newViewer => {
    if (newViewer?.id && newViewer?.profile?.name) {
      trackPlanner({
        id: newViewer.id,
        username: newViewer.profile.name,
      })
    }
  },
  { immediate: true }
)

const tabActivated = ref(false)
useOnTabActivated(
  () => {
    tabActivated.value = true
  },
  { once: true }
)
</script>

<style lang="scss" module>
.layout {
  $root: &;
  $header-height: 60px;
  $content-side-padding: 40px;

  --footer-height: 198px;

  // 付与されたクラスによって値を変えたい場合は、Sass の変数ではなく CSS のカスタムプロパティを使う
  --content-width: #{$content-width};

  // KarteListPageのようにfull-bleedかつCenterdPageContainerを
  // 使う場合もあるので、min-widthは常にside-paddingを含める
  --min-width: #{$content-width + ($content-side-padding * 2)};

  min-width: var(--min-width);
  background-color: $color-background-base;
  isolation: isolate;
  transform: translate(0);

  &--wide {
    --content-width: #{$content-width + 240px};
  }

  &--footerSmall {
    --footer-height: 24px;
  }

  &__alert {
    position: fixed;
    z-index: 3;
    top: 20px;
    left: 50%;
    min-width: 640px;
    height: $header-height;
    transform: translateX(-50%);
  }

  &__content {
    $content-min-height: calc(100vh - $header-height - var(--footer-height));

    min-width: 100%;
    min-height: $content-min-height;
    box-sizing: border-box;
    padding: 32px $content-side-padding;
    isolation: isolate;

    @at-root #{$root}--fullBleed & {
      height: $content-min-height;
      padding: 0;
    }
  }

  &__header {
    position: relative;
    z-index: 1;
    min-width: 100%;
    height: $header-height;
    box-sizing: border-box;
  }

  &__footer {
    min-width: 100%;
    height: var(--footer-height);
    box-sizing: border-box;
  }
}
</style>
